import * as React from "react";

export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "asrt-theme";
const REDUCED_MOTION_KEY = "asrt-reduced-motion";

/**
 * Inject in <head> (before paint) to avoid a theme flash:
 *   <script dangerouslySetInnerHTML={{ __html: themeInitScript }} />
 */
export const themeInitScript = `(function(){try{var k='${STORAGE_KEY}',d=document.documentElement,t=localStorage.getItem(k)||'dark';var m=window.matchMedia('(prefers-color-scheme: dark)');var dark=t==='dark'||(t==='system'&&m.matches);d.classList.toggle('dark',dark);d.style.colorScheme=dark?'dark':'light';var r=localStorage.getItem('${REDUCED_MOTION_KEY}');if(r==='true'){d.classList.add('reduce-motion');d.setAttribute('data-reduced-motion','true');}}catch(e){}})();`;

export interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  reducedMotion: boolean;
  setReducedMotion: (value: boolean) => void;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

function systemPrefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function systemPrefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export interface ThemeProviderProps {
  children?: React.ReactNode;
  /** Initial theme before localStorage is read. Default "system". */
  defaultTheme?: Theme;
  /** Element that receives the `dark` / `reduce-motion` classes. Default documentElement. */
  target?: HTMLElement;
}

/**
 * Class-based dark mode with localStorage persistence, system-preference
 * fallback and a reduced-motion toggle that hosts can surface in settings.
 */
export function ThemeProvider({
  children,
  defaultTheme = "system",
  target,
}: ThemeProviderProps): React.JSX.Element {
  const [theme, setThemeState] = React.useState<Theme>(() => {
    if (typeof window === "undefined") return defaultTheme;
    try {
      return (window.localStorage.getItem(STORAGE_KEY) as Theme | null) ?? defaultTheme;
    } catch {
      return defaultTheme;
    }
  });

  const [reducedMotion, setReducedMotionState] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      const stored = window.localStorage.getItem(REDUCED_MOTION_KEY);
      if (stored !== null) return stored === "true";
    } catch {
      /* ignore */
    }
    return systemPrefersReducedMotion();
  });

  const [systemDark, setSystemDark] = React.useState<boolean>(() => systemPrefersDark());

  const resolvedTheme: ResolvedTheme =
    theme === "system" ? (systemDark ? "dark" : "light") : theme;

  // Track OS theme changes while in "system" mode.
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    setSystemDark(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Apply the class.
  React.useEffect(() => {
    const el = target ?? (typeof document !== "undefined" ? document.documentElement : null);
    if (!el) return;
    el.classList.toggle("dark", resolvedTheme === "dark");
    el.style.colorScheme = resolvedTheme;
  }, [resolvedTheme, target]);

  // Apply reduced-motion (explicit toggle wins over the media query).
  React.useEffect(() => {
    const el = target ?? (typeof document !== "undefined" ? document.documentElement : null);
    if (!el) return;
    el.classList.toggle("reduce-motion", reducedMotion);
    if (reducedMotion) el.setAttribute("data-reduced-motion", "true");
    else el.removeAttribute("data-reduced-motion");
  }, [reducedMotion, target]);

  const setTheme = React.useCallback((next: Theme) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage unavailable (private mode / SSR) */
    }
  }, []);

  const setReducedMotion = React.useCallback((next: boolean) => {
    setReducedMotionState(next);
    try {
      window.localStorage.setItem(REDUCED_MOTION_KEY, String(next));
    } catch {
      /* ignore */
    }
  }, []);

  const value = React.useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme, reducedMotion, setReducedMotion }),
    [theme, resolvedTheme, setTheme, reducedMotion, setReducedMotion],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within <ThemeProvider>");
  return ctx;
}

ThemeProvider.displayName = "ThemeProvider";
